import {
  Box,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { Provider, type Account, type S3Bucket } from '@repo/api-gw-sdk';
import { type Dispatch, type SetStateAction } from 'react';

import { TransactionalTextField } from '@/components/shared/transactionalTextField';

import { EmptySelect } from '../regions/emptySelect';
import SelectPlaceholder from '../selectPlaceholder';

export interface RestoreGenericStorageProps {
  region?: string;
  prefix?: string;
  S3: { Bucket?: string };
  GCS: { Bucket?: string };
  StorageAccount: {
    AccountName?: string;
    ResourceGroup?: string;
    Container?: string;
  };
}

interface BucketSelectionProps<T> extends RestoreGenericStorageProps {
  restoreAccount?: Account;
  buckets: S3Bucket[];
  setBucketSelectionState: Dispatch<
    SetStateAction<
      T & {
        restoreAccount?: Account;
      }
    >
  >;
}

export const BucketSelection = <T extends RestoreGenericStorageProps>(
  props: BucketSelectionProps<T>
) => {
  const { prefix, setBucketSelectionState, buckets } = props;
  const stateKey = {
    [Provider.Aws]: 'S3' as const,
    [Provider.Gcp]: 'GCS' as const,
    [Provider.Azure]: null,
    [Provider.MongoAtlas]: null,
    [Provider.ProviderUnspecified]: null,
  }[props.restoreAccount?.cloudProvider ?? Provider.ProviderUnspecified];

  const setRestoreBucketName = (value: string) => {
    if (!stateKey) {
      return;
    }
    setBucketSelectionState((state) => ({
      ...state,
      [stateKey]: { ...state[stateKey], Bucket: value },
      region: state.region || props.region,
    }));
  };

  const setRestorePrefix = (value: string) => {
    setBucketSelectionState((state) => ({
      ...state,
      prefix: value,
    }));
  };

  return (
    <Box className='mx-[40px] my-[24px]'>
      <Stack direction='row' gap='24px' className='mt-[20px] mb-[24px]'>
        {!buckets?.length ? (
          <EmptySelect title='Bucket' />
        ) : (
          <FormControl size='small' className='flex-1'>
            <FormLabel>Bucket</FormLabel>
            <Select
              data-testid='bucketSelect'
              displayEmpty
              renderValue={(value: string) => {
                if (!value) {
                  return <SelectPlaceholder />;
                }
                const bucket = buckets.find((x) => x.bucketName === value);
                if (!bucket) {
                  return <SelectPlaceholder />;
                }
                return <BucketRegionItem {...bucket} />;
              }}
              className='h-[58px]'
              size='small'
              value={
                (stateKey &&
                  buckets.find((x) => x.bucketName === props[stateKey].Bucket)
                    ?.bucketName) ||
                ''
              }
              onChange={(event) => setRestoreBucketName(event.target.value)}
            >
              {buckets.map((bucket, index) => (
                <MenuItem
                  data-testid={`bucketOption-${index}`}
                  key={bucket.bucketName}
                  value={bucket.bucketName}
                >
                  <BucketRegionItem {...bucket} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <BucketPrefix prefix={prefix} onChange={setRestorePrefix} />
      </Stack>
    </Box>
  );
};

const BucketRegionItem = (props: {
  bucketName: string;
  regionName: string;
}) => {
  return (
    <Stack direction={'column'} className='py-[4px]'>
      <Typography>{props.bucketName}</Typography>
      <Typography variant='body2' className='mt-[4px]'>
        {props.regionName}
      </Typography>
    </Stack>
  );
};

const BucketPrefix = (props: {
  prefix?: string;
  onChange: (value: string) => void;
}) => {
  return (
    <FormControl size='small' className='flex-1'>
      <FormLabel>Prefix (optional)</FormLabel>
      <Stack height='100%' direction='column' justifyContent='space-between'>
        <TransactionalTextField
          initValue={props.prefix || ''}
          onChange={props.onChange}
        />
        <Typography
          paddingTop='8px'
          variant='subtitle1'
          className='font-semibold'
          color='var(--mui-palette-secondary-main)'
        >
          Example: project/wordFiles/
        </Typography>
      </Stack>
    </FormControl>
  );
};

export const getStorageName = (
  bucket?: RestoreGenericStorageProps,
  cloudProvider?: Provider
) => {
  if (!bucket) {
    return;
  }

  switch (cloudProvider) {
    case Provider.Aws:
      return bucket.S3?.Bucket;
    case Provider.Gcp:
      return bucket.GCS?.Bucket;
    case Provider.Azure:
      return bucket.StorageAccount?.AccountName;
  }
};
